import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "../lib/api";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  MousePointerClick,
  ShoppingCart,
  Percent,
  Calendar,
  Download,
  AlertTriangle,
  ChevronRight,
  HelpCircle,
  XCircle,
  RefreshCw,
  UserPlus,
  Users,
  ShieldCheck,
  AlertOctagon,
  Calculator,
  Truck,
  Package,
  Trophy,
  TrendingDown as TrendingDownIcon,
  Zap,
  Activity,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";

const formatK = (val) => {
  if (typeof val !== "number") return val;
  if (val >= 1000000) return (val / 1000000).toFixed(1) + "M";
  if (val >= 10000) return Math.round(val / 1000) + "K";
  if (val >= 1000) return (val / 1000).toFixed(1) + "K";
  return val.toLocaleString("es-ES", { maximumFractionDigits: 2 });
};

// ─── ScoreRow — horizontal scorecard replacing the old 6×KPICard grid ───
const ScoreRow = ({ metrics }) => (
  <div
    className="flex flex-col sm:flex-row border border-[#E8E8E5] rounded-lg overflow-hidden bg-white"
    data-testid="dashboard-score-row"
  >
    {metrics.map((m, i) => (
      <div
        key={i}
        className={`flex-1 px-5 py-4 border-b sm:border-b-0 sm:border-r border-[#E8E8E5] last:border-r-0 last:border-b-0 ${
          i === 0 ? "bg-[#F7F6F3]" : "bg-white"
        }`}
        data-testid={`score-cell-${m.testId || i}`}
      >
        <div className="flex items-center gap-1 mb-2">
          <span className="label-swiss">{m.label}</span>
          {m.tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-3 h-3 text-[#AAAAAA] cursor-help flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-[#0A0A0A] text-white border-[#0A0A0A]">
                  <p className="text-xs">{m.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="font-display font-black text-[26px] leading-none tracking-[-0.04em] text-[#0A0A0A]">
          {m.prefix}
          {m.value}
          {m.unit && (
            <span className="text-[15px] font-normal text-[#888888] ml-0.5">{m.unit}</span>
          )}
        </div>
        {m.sub && (
          <div className="text-[11.5px] text-[#888888] mt-1.5 font-medium">{m.sub}</div>
        )}
      </div>
    ))}
  </div>
);

const AttributionCard = ({ data }) => {
  if (!data || !data.attribution) return null;

  const { attribution, data_sources } = data;
  const hasDiscrepancy =
    attribution.difference.revenue !== 0 || attribution.difference.conversions !== 0;

  const formatNumber = (val) => {
    if (val >= 1000000) return (val / 1000000).toFixed(1) + "M";
    if (val >= 1000) return (val / 1000).toFixed(1) + "K";
    return val?.toLocaleString("es-ES", { maximumFractionDigits: 0 }) || "0";
  };

  const sources = [
    { key: "store", label: "Tienda" },
    { key: "ga4", label: "GA4" },
    { key: "google_ads", label: "Google Ads" },
    { key: "meta_ads", label: "Meta Ads" },
    { key: "awin", label: "Awin" },
  ];

  const cells = [
    { label: "Ing. Tienda", value: `€${formatNumber(attribution.store_revenue)}`, tooltip: "Ingresos facturados en pedidos. No incluye IVA ni transporte." },
    { label: "Ing. GA4", value: `€${formatNumber(attribution.ga4_revenue)}`, tooltip: "Ingresos atribuidos por Google Analytics. Incluye IVA y transporte." },
    { label: "Conv. Tienda", value: formatNumber(attribution.store_conversions), tooltip: "Pedidos reales confirmados en la tienda (estados seleccionados)." },
    { label: "Sesiones GA4", value: formatNumber(attribution.ga4_sessions), tooltip: "Sesiones de usuarios en tu sitio registradas por GA4." },
  ];

  return (
    <div
      className="border border-[#E8E8E5] rounded-lg bg-white overflow-hidden"
      style={hasDiscrepancy ? { borderLeft: "4px solid #B45309" } : undefined}
      data-testid="attribution-card"
    >
      <div className="flex items-center justify-between px-5 py-3 bg-[#F7F6F3] border-b border-[#E8E8E5]">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="label-swiss">Comparación de Atribución</span>
          <span className="text-[12.5px] font-semibold text-[#0A0A0A]">
            {hasDiscrepancy
              ? `GA4: ${attribution.difference.conversions > 0 ? "+" : ""}${formatNumber(
                  attribution.difference.conversions
                )} conv. vs Tienda`
              : "Datos sincronizados"}
          </span>
        </div>
        {hasDiscrepancy && <AlertTriangle className="w-4 h-4 text-[#B45309]" />}
      </div>

      <div className="flex flex-col sm:flex-row">
        {cells.map((c, i) => (
          <div
            key={c.label}
            className="flex-1 px-5 py-4 border-b sm:border-b-0 sm:border-r border-[#E8E8E5] last:border-r-0 last:border-b-0"
          >
            <div className="flex items-center gap-1 mb-1.5">
              <span className="label-swiss">{c.label}</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-3 h-3 text-[#AAAAAA] cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-[#0A0A0A] text-white border-[#0A0A0A]">
                    <p className="text-xs">{c.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="font-mono text-[18px] font-semibold text-[#0A0A0A]">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-4 px-5 py-2.5 border-t border-[#F0F0ED]">
        {sources.map((s) => {
          const ok = !!data_sources?.[s.key];
          return (
            <span
              key={s.key}
              className="flex items-center gap-1.5 text-[11.5px]"
              style={{ color: ok ? "#1A7A3C" : "#AAAAAA" }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: ok ? "#1A7A3C" : "#D0D0CC" }}
              />
              {s.label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

const PLATFORM_META = {
  google_ads: { name: "Google Ads", bg: "#E8F0FE", color: "#4285F4", sub: "Search & Shopping" },
  meta_ads: { name: "Meta Ads", bg: "#E7F0FE", color: "#1877F2", sub: "Facebook & Instagram" },
  tiktok_ads: { name: "TikTok Ads", bg: "#0A0A0A", color: "#FFFFFF", sub: "Video & Shopping" },
  awin: { name: "Awin Affiliates", bg: "#E7F5EE", color: "#1A7A3C", sub: "Afiliados" },
};

const PlatformsTable = ({ platforms, onRowClick }) => {
  const connected = platforms.filter((p) => p.connected).length;
  return (
    <div
      className="border border-[#E8E8E5] rounded-lg overflow-hidden bg-white"
      data-testid="platforms-table"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8E8E5]">
        <div>
          <h3 className="text-[13px] font-semibold text-[#0A0A0A]">Plataformas</h3>
          <p className="text-[11.5px] text-[#AAAAAA] mt-0.5">
            {connected} de {platforms.length} conectadas
          </p>
        </div>
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[#F7F6F3] border-b border-[#E8E8E5]">
            <th className="label-swiss text-left px-5 py-2.5">Plataforma</th>
            <th className="label-swiss text-right px-5 py-2.5">Gasto</th>
            <th className="label-swiss text-right px-5 py-2.5">ROAS</th>
            <th className="label-swiss text-right px-5 py-2.5">Conversiones</th>
            <th className="label-swiss text-right px-5 py-2.5">CPA</th>
          </tr>
        </thead>
        <tbody>
          {platforms.map((p) => {
            const meta = PLATFORM_META[p.platform] || { name: p.platform, bg: "#F0F0ED", color: "#888", sub: "" };
            const roasGreen = p.roas > 4;
            return (
              <tr
                key={p.platform}
                className={`border-b border-[#F0F0ED] last:border-0 transition-colors ${
                  p.connected ? "hover:bg-[#FAFAF8] cursor-pointer" : "opacity-50"
                }`}
                onClick={() => p.connected && onRowClick && onRowClick(p.platform)}
                data-testid={`platform-row-${p.platform}`}
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-[5px] flex items-center justify-center flex-shrink-0"
                      style={{ background: meta.bg }}
                    >
                      <BarChart3 className="w-3 h-3" style={{ color: meta.color }} strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="text-[13.5px] font-semibold text-[#0A0A0A]">{meta.name}</div>
                      <div className="text-[11px] text-[#AAAAAA]">{meta.sub}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-right font-mono text-[13px] font-medium text-[#0A0A0A]">
                  {p.connected ? `€${formatK(p.spend)}` : "—"}
                </td>
                <td
                  className={`px-5 py-3 text-right font-mono text-[13px] font-medium ${
                    roasGreen ? "text-[#1A7A3C]" : "text-[#0A0A0A]"
                  }`}
                >
                  {p.connected ? `${p.roas?.toFixed(2)}x` : "—"}
                </td>
                <td className="px-5 py-3 text-right font-mono text-[13px] font-medium text-[#0A0A0A]">
                  {p.connected ? p.conversions?.toLocaleString("es-ES") : "—"}
                </td>
                <td className="px-5 py-3 text-right font-mono text-[13px] font-medium text-[#0A0A0A]">
                  {p.connected ? (
                    `€${p.cpa?.toFixed(2)}`
                  ) : (
                    <Link
                      to="/integrations"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[11.5px] font-sans text-[#C8192C] hover:underline"
                    >
                      Conectar →
                    </Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Cushion descriptor (text only — no badge styles) ─────────────────────
const getCushionLabel = (ratio) => {
  if (ratio == null) return null;
  if (ratio > 1.5) return "por encima de break-even";
  if (ratio > 1.2) return "prudente";
  if (ratio > 1.05) return "cerca del break-even";
  return "crítico — por debajo de break-even";
};

// ─── Financial Status Banner — ledger style ───────────────────────────────
const STATUS_COLOR = {
  safe: "#1A7A3C",
  risk: "#B45309",
  danger: "#C8192C",
};
const STATUS_LABEL = {
  safe: "SAFE",
  risk: "RISK",
  danger: "DANGER",
};

const FinancialStatusBanner = ({ operating }) => {
  const statusColor = STATUS_COLOR[operating.financial_status] || STATUS_COLOR.risk;
  const statusLabel = STATUS_LABEL[operating.financial_status] || "RISK";
  const fmtEur = (v) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v || 0);
  const fmtEurInt = (v) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
  const cushionLabel = getCushionLabel(operating.financial_cushion_ratio);

  const kpis = [
    { label: "Margen Operativo", value: `${operating.operating_margin_pct?.toFixed(1)}%`, colored: true },
    { label: "Costes Fijos", value: fmtEurInt(operating.total_fixed_costs) },
    { label: "Bº Operativo", value: fmtEurInt(operating.operating_profit), colored: true },
    ...(operating.break_even_revenue != null
      ? [{ label: "Break-even", value: fmtEurInt(operating.break_even_revenue) }]
      : []),
    {
      label: "Bº / Pedido",
      value: operating.operating_profit_per_order != null ? fmtEur(operating.operating_profit_per_order) : "—",
      colored: true,
    },
  ];

  return (
    <div
      className="border border-[#E8E8E5] rounded-lg overflow-hidden bg-white"
      style={{ borderLeft: `4px solid ${statusColor}` }}
      data-testid="financial-status-banner"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#F7F6F3] border-b border-[#E8E8E5]">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="label-swiss">Estado Operativo</span>
          <span
            className="font-display font-black text-[11px] tracking-[0.06em] uppercase pb-0.5 border-b-2"
            style={{ color: statusColor, borderColor: statusColor }}
            data-testid="financial-status-label"
          >
            {statusLabel}
          </span>
          {operating.financial_cushion_ratio != null && (
            <span className="text-[11.5px] text-[#888888]" data-testid="financial-cushion-badge">
              Colchón {operating.financial_cushion_ratio.toFixed(2)}x · {cushionLabel}
            </span>
          )}
        </div>
      </div>
      {/* Body */}
      <div className="flex flex-col sm:flex-row">
        {kpis.map((kpi, i) => (
          <div
            key={i}
            className="flex-1 px-5 py-4 border-b sm:border-b-0 sm:border-r border-[#E8E8E5] last:border-r-0 last:border-b-0"
          >
            <div className="label-swiss mb-1.5">{kpi.label}</div>
            <div
              className="font-mono text-[17px] font-semibold"
              style={kpi.colored ? { color: statusColor } : { color: "#0A0A0A" }}
            >
              {kpi.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Top / Bottom Products — editorial list with fade ─────────────────────
const TopBottomProductsBlock = ({ topProducts, bottomProducts, loading }) => {
  const fmtProfit = (val) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val || 0);

  const fadeOpacity = (i, total) => {
    if (total <= 1) return 1;
    return Math.max(0.3, 1 - (i / total) * 0.7);
  };

  const List = ({ title, subtitle, items, sortDir, color, testId }) => (
    <div className="border border-[#E8E8E5] rounded-lg bg-white overflow-hidden" data-testid={testId}>
      <div className="px-5 py-3 border-b border-[#E8E8E5] flex items-center justify-between">
        <div>
          <span className="label-swiss">{subtitle}</span>
          <h3 className="text-[13px] font-semibold text-[#0A0A0A] mt-0.5">{title}</h3>
        </div>
        <span className="label-swiss">Últimos 30 días</span>
      </div>
      <div className="divide-y divide-[#F0F0ED]">
        {loading ? (
          <div className="px-5 py-8 text-center text-[#AAAAAA] text-[12px]">Cargando…</div>
        ) : items?.length > 0 ? (
          items.map((p, i) => (
            <div
              key={p.product_uid || i}
              className="flex items-center gap-4 px-5 py-2.5"
              style={{ opacity: fadeOpacity(i, items.length) }}
            >
              <span className="font-mono text-[10.5px] text-[#AAAAAA] w-6 flex-shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-[#0A0A0A] truncate leading-tight">
                  {p.name || p.reference || "—"}
                </p>
                {p.reference && p.name && (
                  <p className="text-[11px] text-[#AAAAAA] truncate leading-tight">{p.reference}</p>
                )}
              </div>
              <span
                className="font-mono text-[13px] font-semibold flex-shrink-0"
                style={{ color: p.net_profit < 0 ? "#C8192C" : color }}
              >
                {p.net_profit > 0 ? "+" : ""}
                {fmtProfit(p.net_profit)}
              </span>
            </div>
          ))
        ) : (
          <div className="px-5 py-8 text-center text-[#AAAAAA] text-[12px]">
            Sin datos para los últimos 30 días
          </div>
        )}
      </div>
      {items?.length > 0 && (
        <div className="px-5 py-2.5 border-t border-[#F0F0ED]">
          <Link
            to={`/analytics/products?sort_by=net_profit&sort_order=${sortDir}&preset=last30`}
            className="text-[11.5px] text-[#888888] hover:text-[#0A0A0A] inline-flex items-center gap-1"
          >
            Ver todos los productos <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="top-bottom-products-block">
      <List
        title="Top 10 por Beneficio Neto"
        subtitle="Mejores productos"
        items={topProducts}
        sortDir="desc"
        color="#1A7A3C"
        testId="top-products-card"
      />
      <List
        title="Bottom 10 por Beneficio Neto"
        subtitle="Productos perdedores"
        items={bottomProducts}
        sortDir="asc"
        color="#0A0A0A"
        testId="bottom-products-card"
      />
    </div>
  );
};

// ─── New Customer Economics — white ledger card ───────────────────────────
const NewCustomerEconomicsBlock = ({ data }) => {
  if (!data || !data.has_data) return null;

  const fmtEur2 = (v) => {
    const abs = new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(v || 0));
    return (v || 0) < 0 ? `-${abs}` : abs;
  };
  const fmtEur0 = (v) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

  const cm = data.contribution_margin_per_order;
  const gm = data.gross_margin_per_order;
  const buf = data.roas_gap || 0;
  const payback = data.payback_period;

  const status = cm > 0.5 ? "profitable" : cm >= -0.5 ? "breakeven" : "losing";
  const palette = {
    profitable: { color: "#1A7A3C", label: "Profitable · Ads escalables", border: "#1A7A3C" },
    breakeven: { color: "#B45309", label: "Break-even", border: "#B45309" },
    losing: { color: "#C8192C", label: "Losing money", border: "#C8192C" },
  }[status];

  const numColor = (v) => (v > 0 ? "#1A7A3C" : v === 0 ? "#B45309" : "#C8192C");

  return (
    <div
      className="border border-[#E8E8E5] rounded-lg bg-white overflow-hidden"
      style={{ borderLeft: `4px solid ${palette.border}` }}
      data-testid="new-customer-economics-block"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#F7F6F3] border-b border-[#E8E8E5]">
        <span className="label-swiss">New Customer Economics</span>
        <span className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: palette.color }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: palette.color }} />
          {palette.label}
        </span>
      </div>

      {/* Decision row — Contribution / Break-even ROAS / Buffer */}
      <div className="flex flex-col sm:flex-row border-b border-[#E8E8E5]">
        <div className="flex-1 px-5 py-4 border-b sm:border-b-0 sm:border-r border-[#E8E8E5]">
          <div className="label-swiss mb-1.5">Contribution Margin</div>
          <div className="font-display font-black text-[24px] leading-none tracking-[-0.04em] font-mono"
            style={{ color: numColor(cm), fontFamily: "JetBrains Mono" }}
            data-testid="nce-contribution-margin">
            {fmtEur2(cm)}
          </div>
          <div className="text-[11.5px] text-[#888888] mt-1.5">por pedido</div>
        </div>
        <div className="flex-1 px-5 py-4 border-b sm:border-b-0 sm:border-r border-[#E8E8E5]">
          <div className="label-swiss mb-1.5 flex items-center gap-1">
            Break-Even ROAS
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-3 h-3 text-[#AAAAAA] cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="bg-[#0A0A0A] text-white border-[#0A0A0A] max-w-xs">
                  <p className="text-xs">ROAS mínimo para cubrir COGS = 1 ÷ Gross Margin ratio</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="font-mono text-[24px] font-bold text-[#0A0A0A]" data-testid="break-even-roas">
            {data.break_even_roas.toFixed(2)}x
          </div>
          <div className="text-[11.5px] text-[#888888] mt-1.5">
            ROAS actual: <span className="text-[#0A0A0A] font-medium">{data.roas_new.toFixed(2)}x</span>
          </div>
        </div>
        <div className="flex-1 px-5 py-4">
          <div className="label-swiss mb-1.5">Profit Buffer</div>
          <div className="font-mono text-[24px] font-bold" style={{ color: palette.color }} data-testid="nce-profit-buffer">
            {buf >= 0 ? "+" : ""}
            {buf.toFixed(2)}x
          </div>
          <div className="text-[11.5px] mt-1.5" style={{ color: palette.color, opacity: 0.8 }}>
            {palette.label}
          </div>
        </div>
      </div>

      {/* Insight banner */}
      <div className="px-5 py-3 bg-[#F7F6F3] border-b border-[#E8E8E5] text-[12.5px]">
        {cm < 0 ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-1">
            <span className="font-semibold" style={{ color: "#C8192C" }}>
              Pierdes {fmtEur2(Math.abs(cm))} por pedido
            </span>
            <span className="text-[#404040]">
              Subir ROAS <span className="font-semibold text-[#B45309]">{Math.abs(buf).toFixed(2)}x</span> o
              reducir CAC <span className="font-semibold text-[#B45309]">{fmtEur2(Math.abs(cm))}</span> para break-even
            </span>
          </div>
        ) : cm < 0.5 ? (
          <span className="font-semibold" style={{ color: "#B45309" }}>
            En break-even — cualquier mejora en ROAS o CAC genera beneficio.
          </span>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-1">
            <span className="font-semibold" style={{ color: "#1A7A3C" }}>
              Ganas {fmtEur2(cm)} por pedido
            </span>
            <span className="text-[#404040]">
              ROAS {data.roas_new.toFixed(2)}x supera break-even en{" "}
              <span className="font-semibold" style={{ color: "#1A7A3C" }}>
                +{buf.toFixed(2)}x
              </span>
              {" "}— ads escalables
            </span>
          </div>
        )}
      </div>

      {/* Unit economics — P&L style */}
      <div className="px-5 py-4" data-testid="unit-economics-breakdown">
        <div className="label-swiss mb-3">Unit Economics</div>
        <div className="font-mono text-[13px] space-y-1.5 max-w-md">
          <div className="flex justify-between text-[#0A0A0A]">
            <span>AOV nuevos clientes</span>
            <span className="font-semibold" data-testid="nce-aov">{fmtEur2(data.aov_new)}</span>
          </div>
          <div className="flex justify-between text-[#888888]">
            <span>− Coste producto (COGS)</span>
            <span>−{fmtEur2(data.avg_product_cost_per_order)}</span>
          </div>
          <div className="flex justify-between border-t border-[#E8E8E5] pt-1.5 font-semibold"
            style={{ color: numColor(gm) }}>
            <span>= Gross Margin</span>
            <span>{fmtEur2(gm)}</span>
          </div>
          <div className="flex justify-between text-[#888888]">
            <span>− CAC</span>
            <span data-testid="nce-cac">−{fmtEur2(data.cac)}</span>
          </div>
          <div className="flex justify-between border-t-[1.5px] border-double border-[#0A0A0A] pt-1.5 font-bold"
            style={{ color: numColor(cm) }}>
            <span>= Contribution Margin</span>
            <span>{fmtEur2(cm)}</span>
          </div>
          <div className="flex justify-between border-t border-[#F0F0ED] pt-2 text-[#888888] text-[12px]">
            <span>Payback orders</span>
            <span
              className="font-semibold"
              style={{
                color:
                  payback == null
                    ? "#C8192C"
                    : payback <= 2
                    ? "#1A7A3C"
                    : payback <= 4
                    ? "#B45309"
                    : "#C8192C",
              }}
              data-testid="nce-payback-period"
            >
              {payback != null ? `${payback.toFixed(1)} orders` : "∞"}
            </span>
          </div>
        </div>
      </div>

      {/* Context footer */}
      <div className="flex items-center gap-4 px-5 py-3 border-t border-[#F0F0ED] text-[11.5px] text-[#888888]">
        <div className="flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          <span data-testid="nce-new-customers">
            {data.new_customers.toLocaleString("es-ES")} nuevos clientes
          </span>
        </div>
        {data.new_customer_revenue_pct > 0 && (
          <div className="flex items-center gap-1.5">
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span>
              {data.new_customer_revenue_pct}% del revenue{" "}
              <span className="text-[#AAAAAA]">({fmtEur0(data.new_customer_revenue)})</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Customer LTV Block ────────────────────────────────────────────────────
const CustomerLTVBlock = ({ data }) => {
  // Show "computing" skeleton when snapshot is being generated
  if (!data) return null;
  if (!data.has_data && data.computing) {
    return (
      <div className="border border-[#E8E8E5] rounded-lg bg-white overflow-hidden" data-testid="customer-ltv-block-computing">
        <div className="flex items-center gap-3 px-5 py-4">
          <div className="w-4 h-4 border-2 border-[#C8192C] border-t-transparent rounded-full animate-spin" />
          <span className="text-[13px] text-[#888888]">Calculando LTV… esto ocurre una vez. Actualiza en unos segundos.</span>
        </div>
      </div>
    );
  }
  if (!data.has_data) return null;

  const marginRatio = data.ltv_margin_cac_ratio || 0;
  const statusColor =
    marginRatio >= 3 ? "#1A7A3C" :
    marginRatio >= 1 ? "#0A0A0A" :
    marginRatio > 0 ? "#B45309" : "#888888";
  const statusText =
    marginRatio >= 3 ? `${marginRatio.toFixed(1)}x Margen/CAC · Escalable` :
    marginRatio >= 1 ? `${marginRatio.toFixed(1)}x Margen/CAC · Sostenible` :
    marginRatio > 0 ? `${marginRatio.toFixed(1)}x Margen/CAC · Riesgo` :
    "Sin datos de CAC";

  const fmtEur0 = (v) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

  const paybackStatusLabel = {
    excellent: { label: "Excelente", color: "#1A7A3C" },
    good: { label: "Bueno", color: "#0A0A0A" },
    normal: { label: "Normal", color: "#B45309" },
    slow: { label: "Lento", color: "#C8192C" },
  }[data.cac_payback_status];

  const cells = [
    { label: "LTV", value: fmtEur0(data.ltv), sub: "ingresos por cliente", testId: "ltv-value" },
    { label: "LTV Margen", value: fmtEur0(data.ltv_margin), sub: "margen por cliente", testId: "ltv-margin-value" },
    { label: "Pedidos / Cliente", value: (data.orders_per_customer || 0).toFixed(1), sub: "promedio", testId: "ltv-orders-per-customer" },
    { label: "Repeat Rate", value: `${(data.repeat_rate || 0).toFixed(1)}%`, sub: "2+ pedidos", color: "#1A7A3C", testId: "ltv-repeat-rate" },
    { label: "Margen / CAC", value: marginRatio > 0 ? `${marginRatio.toFixed(1)}x` : "—", sub: statusText.split(" · ")[1] || "—", color: statusColor, testId: "ltv-margin-cac-ratio" },
    ...(data.cac_payback_days != null
      ? [{
          label: "CAC Payback",
          value: `${data.cac_payback_days}d`,
          sub: `~${(data.cac_payback_days / 30.44).toFixed(1)} meses${paybackStatusLabel ? ` · ${paybackStatusLabel.label}` : ""}`,
          color: paybackStatusLabel?.color,
          testId: "cac-payback-days",
        }]
      : []),
  ];

  return (
    <div className="border border-[#E8E8E5] rounded-lg bg-white overflow-hidden" data-testid="customer-ltv-block">
      <div className="flex items-center justify-between px-5 py-3 bg-[#F7F6F3] border-b border-[#E8E8E5]">
        <div className="flex items-center gap-3">
          <span className="label-swiss">Customer Lifetime Value</span>
          <span className="text-[11.5px] text-[#AAAAAA]">(histórico total)</span>
        </div>
        <span className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: statusColor }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
          {statusText}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        {cells.map((c, i) => (
          <div
            key={c.label}
            className={`px-5 py-4 border-b md:border-b border-[#E8E8E5] ${
              i % 6 !== 5 ? "md:border-r" : ""
            } ${i >= cells.length - (cells.length % 6 || 6) ? "lg:border-b-0" : ""}`}
            data-testid={`ltv-cell-${c.testId}`}
          >
            <div className="label-swiss mb-1.5">{c.label}</div>
            <div className="font-mono text-[20px] font-bold" style={{ color: c.color || "#0A0A0A" }} data-testid={c.testId}>
              {c.value}
            </div>
            <div className="text-[11px] text-[#888888] mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="px-5 py-2.5 border-t border-[#F0F0ED] flex justify-end">
        <Link
          to="/analytics/customer-ltv"
          className="text-[11.5px] text-[#888888] hover:text-[#0A0A0A] inline-flex items-center gap-1"
        >
          Ver análisis completo de cohortes <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};

// ─── Customer Profitability Insight Block ──────────────────────────────────
const CustomerProfitabilityInsightBlock = ({ nce, ltv }) => {
  if (!nce?.has_data || !ltv?.has_data) return null;

  const cac = nce.cac || 0;
  const gross_margin_per_order = nce.gross_margin_per_order || 0;
  const ltv_margin = ltv.ltv_margin || 0;
  const ltv_margin_cac_ratio = ltv.ltv_margin_cac_ratio || 0;
  const contribution_margin = nce.contribution_margin_per_order || 0;
  const payback_days = ltv.cac_payback_days;
  const payback_status = ltv.cac_payback_status || "unknown";
  const payback_message = ltv.cac_payback_message || "";

  const customer_lifetime_profit = ltv_margin - cac;
  const orders_to_profit = (gross_margin_per_order > 0 && cac > 0)
    ? Math.ceil(cac / gross_margin_per_order)
    : null;

  // Determine case — uses margin-based ratio for acquisition decisions
  let insightCase;
  if (ltv_margin_cac_ratio >= 3 && customer_lifetime_profit > 0) {
    insightCase = "excellent";
  } else if (customer_lifetime_profit > 0 && contribution_margin < 0) {
    insightCase = "recovers";
  } else if (customer_lifetime_profit > 0) {
    insightCase = "profitable";
  } else {
    insightCase = "unprofitable";
  }

  const cases = {
    excellent: {
      border: "border-emerald-500", bg: "bg-emerald-500/5",
      iconColor: "text-emerald-400", Icon: Zap,
      title: `Los clientes generan ${ltv_margin_cac_ratio.toFixed(1)}x su coste de adquisición (en margen)`,
      subtitle: "La adquisición puede escalarse con seguridad.",
    },
    profitable: {
      border: "border-emerald-500", bg: "bg-emerald-500/5",
      iconColor: "text-emerald-400", Icon: TrendingUp,
      title: "Los clientes son rentables en el primer pedido y a lo largo de su vida",
      subtitle: "Sólida economía de adquisición.",
    },
    recovers: {
      border: "border-blue-500", bg: "bg-blue-500/5",
      iconColor: "text-blue-400", Icon: Activity,
      title: orders_to_profit
        ? `El primer pedido no es rentable, pero los clientes se recuperan en ~${orders_to_profit} pedidos`
        : "El primer pedido no es rentable, pero los clientes son rentables a largo plazo",
      subtitle: "La adquisición es rentable a largo plazo.",
    },
    unprofitable: {
      border: "border-red-500", bg: "bg-red-500/5",
      iconColor: "text-red-400", Icon: AlertOctagon,
      title: "Los clientes siguen siendo no rentables a lo largo de su vida",
      subtitle: "Reduce el CAC o aumenta el AOV para alcanzar la rentabilidad.",
    },
  }[insightCase];

  const { Icon } = cases;

  // Payback badge colors
  const paybackColors = {
    excellent: "text-emerald-400",
    good:      "text-blue-400",
    normal:    "text-amber-400",
    slow:      "text-red-400",
    unknown:   "text-slate-400",
  };

  return (
    <div
      className={`rounded-xl border-l-4 px-5 py-4 ${cases.bg} ${cases.border} bg-slate-900`}
      data-testid="customer-profitability-insight"
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 flex-shrink-0 ${cases.iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${cases.iconColor}`}>{cases.title}</p>
              <p className="text-xs text-slate-400 mt-0.5">{cases.subtitle}</p>
              {/* Payback insight line */}
              {payback_message && (
                <p className={`text-xs mt-1.5 ${paybackColors[payback_status]}`}>
                  {payback_message}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4 flex-shrink-0 font-mono text-xs">
              <div className="text-center">
                <p className="text-slate-500 text-xs mb-0.5">Lifetime Profit</p>
                <p className={`font-bold text-sm ${customer_lifetime_profit > 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {customer_lifetime_profit >= 0 ? "+" : ""}
                  {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(customer_lifetime_profit)}
                </p>
              </div>
              {ltv_margin_cac_ratio > 0 && (
                <div className="text-center">
                  <p className="text-slate-500 text-xs mb-0.5">Margen / CAC</p>
                  <p className={`font-bold text-sm ${ltv_margin_cac_ratio >= 3 ? "text-emerald-400" : ltv_margin_cac_ratio >= 1 ? "text-blue-400" : "text-amber-400"}`}>
                    {ltv_margin_cac_ratio.toFixed(1)}x
                  </p>
                </div>
              )}
              {payback_days != null && (
                <div className="text-center">
                  <p className="text-slate-500 text-xs mb-0.5">Payback</p>
                  <p className={`font-bold text-sm ${paybackColors[payback_status]}`}>
                    {payback_days}d
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [topBottomData, setTopBottomData] = useState(null);
  const [topBottomLoading, setTopBottomLoading] = useState(true);

  // Read optional ?start=&end= URL params (e.g. from Annual Summary click-through)
  const getInitialDateRange = () => {
    const params = new URLSearchParams(location.search);
    const startParam = params.get("start");
    const endParam = params.get("end");
    if (startParam && endParam) {
      return { from: new Date(startParam + "T00:00:00"), to: new Date(endParam + "T00:00:00") };
    }
    return { from: subDays(new Date(), 30), to: new Date() };
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [pendingDateRange, setPendingDateRange] = useState(getInitialDateRange);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  // AbortController refs — cancel in-flight requests when dates change
  const overviewAbortRef = useRef(null);
  const topBottomAbortRef = useRef(null);
  // Track whether we already have a request in-flight for this date range
  const overviewRequestedRef = useRef(null);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  useEffect(() => {
    fetchTopBottomProducts();
  }, []);

  const fetchTopBottomProducts = useCallback(async (forceRefresh = false) => {
    // Cancel previous in-flight request
    topBottomAbortRef.current?.abort();
    topBottomAbortRef.current = new AbortController();

    try {
      setTopBottomLoading(true);
      const token = localStorage.getItem("auth_token");
      const params = forceRefresh ? { force_refresh: true } : {};
      const response = await axios.get(`${API}/analytics/top-bottom-products`, {
        params,
        signal: topBottomAbortRef.current.signal,
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setTopBottomData(response.data);
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") return;
      console.error("Error fetching top/bottom products:", error);
    } finally {
      setTopBottomLoading(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    const token = localStorage.getItem("auth_token");

    if (!dateRange.from) {
      toast.error("Selecciona una fecha");
      setLoading(false);
      return;
    }

    const startDate = dateRange.from;
    const endDate   = dateRange.to || dateRange.from;

    // Build a stable key for deduplication
    const requestKey = `${format(startDate, "yyyy-MM-dd")}:${format(endDate, "yyyy-MM-dd")}:${forceRefresh}`;

    // Cancel any previous in-flight overview request
    overviewAbortRef.current?.abort();
    overviewAbortRef.current = new AbortController();
    overviewRequestedRef.current = requestKey;

    try {
      const response = await axios.get(`${API}/analytics/overview`, {
        params: {
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date:   format(endDate,   "yyyy-MM-dd"),
          ...(forceRefresh ? { force_refresh: true } : {}),
        },
        signal: overviewAbortRef.current.signal,
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 90000, // 90s — analytics can be slow on large datasets
      });

      // If LTV is still computing, schedule a re-fetch in 10s to get fresh data
      // without blocking or flooding with retries
      if (response.data?.customer_ltv?.computing) {
        setTimeout(() => {
          // Only re-fetch if the user hasn't changed dates in the meantime
          if (overviewRequestedRef.current === requestKey) {
            fetchAnalytics(false);
          }
        }, 10000);
      }

      setData(response.data);
      setLoading(false);
    } catch (error) {
      // Ignore aborted requests (user changed dates)
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") return;
      console.error("Error fetching analytics:", error);
      toast.error("Error al cargar los datos");
      setLoading(false);
    }
  }, [dateRange]);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/export/csv`, {
        params: {
          start_date: format(dateRange.from, "yyyy-MM-dd"),
          end_date: format(dateRange.to, "yyyy-MM-dd")
        },
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `profitsystem_export_${format(dateRange.from, "yyyy-MM-dd")}_${format(dateRange.to, "yyyy-MM-dd")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Reporte exportado correctamente");
    } catch (error) {
      toast.error("Error al exportar el reporte");
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-xl text-sm">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-slate-300">
              {entry.name}: <span className="font-mono text-white">{entry.name === 'revenue' || entry.name === 'spend' ? '€' : ''}{entry.value?.toLocaleString('es-ES')}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-1">
          <div>
            <h1 className="text-[28px] font-display font-black tracking-[-0.04em] text-[#0A0A0A] leading-tight">
              Dashboard
            </h1>
            <p className="text-[12.5px] text-[#888888] mt-0.5">
              {user?.name ? `Hola, ${user.name}` : "Visión consolidada"}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Popover open={datePopoverOpen} onOpenChange={(open) => { setDatePopoverOpen(open); if (open) setPendingDateRange(dateRange); }}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="border-[#E8E8E5] text-[#404040] hover:bg-[#F7F6F3] hover:text-[#0A0A0A] font-medium text-[12.5px] h-9"
                  data-testid="date-picker-btn"
                >
                  <Calendar className="w-3.5 h-3.5 mr-2" />
                  {format(dateRange.from, "d MMM", { locale: es })} – {format(dateRange.to, "d MMM yyyy", { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="flex">
                  {/* Quick date options */}
                  <div className="border-r border-slate-200 p-2 space-y-1 min-w-[180px]">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1">Accesos rápidos</p>
                    {[
                      { label: "Hoy", from: new Date(), to: new Date() },
                      { label: "Ayer", from: subDays(new Date(), 1), to: subDays(new Date(), 1) },
                      { label: "Últimos 7 días", from: subDays(new Date(), 6), to: new Date() },
                      { label: "Últimos 14 días", from: subDays(new Date(), 13), to: new Date() },
                      { label: "Últimos 30 días", from: subDays(new Date(), 29), to: new Date() },
                      { label: "Esta semana", from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: new Date() },
                      { label: "Semana pasada", from: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }) },
                      { label: "Este mes", from: startOfMonth(new Date()), to: new Date() },
                      { label: "Mes pasado", from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) },
                    ].map((preset) => (
                      <button
                        key={preset.label}
                        onClick={() => {
                          const range = { from: preset.from, to: preset.to };
                          setDateRange(range);
                          setPendingDateRange(range);
                          setDatePopoverOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  {/* Calendar */}
                  <div className="p-2 flex flex-col">
                    <CalendarComponent
                      mode="range"
                      selected={pendingDateRange}
                      onSelect={(range) => range && setPendingDateRange(range)}
                      numberOfMonths={2}
                      locale={es}
                    />
                    <div className="border-t border-slate-200 pt-2 mt-1 flex justify-end">
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={!pendingDateRange.from}
                        data-testid="apply-date-btn"
                        onClick={() => {
                          const range = { from: pendingDateRange.from, to: pendingDateRange.to || pendingDateRange.from };
                          setDateRange(range);
                          setPendingDateRange(range);
                          setDatePopoverOpen(false);
                        }}
                      >
                        Aplicar
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Button
              variant="outline"
              className="border-[#E8E8E5] text-[#404040] hover:bg-[#F7F6F3] hover:text-[#0A0A0A] font-medium text-[12.5px] h-9"
              onClick={handleExport}
              data-testid="export-btn"
            >
              <Download className="w-3.5 h-3.5 mr-2" />
              Exportar
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C8192C]"></div>
          </div>
        ) : data ? (
          <>
            {/* Attribution Alert */}
            <AttributionCard data={data} />

            {/* KPI Scorecard Row */}
            <ScoreRow
              metrics={[
                {
                  label: "Ingresos",
                  prefix: "€",
                  value: formatK(data.summary.total_revenue),
                  testId: "revenue",
                  tooltip: "Ingresos de productos vendidos (sin IVA, sin transporte, con descuentos). Fuente: tienda conectada.",
                },
                {
                  label: "ROAS",
                  value: data.summary.roas?.toFixed(2),
                  unit: "x",
                  testId: "roas",
                  tooltip: "Return On Ad Spend. Ingresos ÷ Gasto publicitario.",
                },
                {
                  label: "Conversiones",
                  value: data.summary.total_conversions?.toLocaleString("es-ES"),
                  testId: "conversions",
                  tooltip: "Pedidos confirmados (estados seleccionados). Fuente: tienda conectada.",
                },
                {
                  label: "CPA",
                  prefix: "€",
                  value: data.summary.cpa?.toFixed(2),
                  testId: "cpa",
                  tooltip: "Coste Por Adquisición. Gasto publicitario ÷ Conversiones.",
                },
                {
                  label: "Gasto Total",
                  prefix: "€",
                  value: formatK(data.summary.total_spend),
                  sub: `${data.summary.spend_percentage?.toFixed(1)}% / ingresos`,
                  testId: "spend",
                  tooltip: "Gasto total en publicidad. Suma de todas las plataformas conectadas.",
                },
              ]}
            />

            {/* New Customer Economics Block */}
            <NewCustomerEconomicsBlock data={data.new_customer_economics} />

            {/* Customer Lifetime Value Block */}
            <CustomerLTVBlock data={data.customer_ltv} />

            {/* Customer Profitability Insight — connects NCE + LTV */}
            <CustomerProfitabilityInsightBlock
              nce={data.new_customer_economics}
              ltv={data.customer_ltv}
            />

            {/* Customer Type KPIs — New vs Returning */}
            {/* Customer type — flat white cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-[#E8E8E5] rounded-lg bg-white p-5" data-testid="new-orders-card">
                <div className="flex items-center gap-1 mb-2">
                  <span className="label-swiss">Pedidos Nuevos</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-[#AAAAAA] cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-[#0A0A0A] text-white border-[#0A0A0A]">
                        <p className="text-xs">Pedidos validados donde el cliente compra por primera vez en la tienda.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="font-display font-black text-[26px] leading-none tracking-[-0.04em] text-[#0A0A0A]">
                  {(data.customer_type?.new_orders || 0).toLocaleString("es-ES")}
                </div>
                <p className="text-[11.5px] text-[#888888] mt-1.5">
                  {data.customer_type?.new_orders_pct || 0}% del total validado
                </p>
                <div className="mt-3 bg-[#F0F0ED] h-1">
                  <div
                    className="bg-[#0A0A0A] h-1 transition-all"
                    style={{ width: `${data.customer_type?.new_orders_pct || 0}%` }}
                  />
                </div>
              </div>

              <div className="border border-[#E8E8E5] rounded-lg bg-white p-5" data-testid="returning-orders-card">
                <div className="flex items-center gap-1 mb-2">
                  <span className="label-swiss">Pedidos Recurrentes</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-[#AAAAAA] cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-[#0A0A0A] text-white border-[#0A0A0A]">
                        <p className="text-xs">Pedidos validados de clientes que ya habían comprado antes.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="font-display font-black text-[26px] leading-none tracking-[-0.04em] text-[#0A0A0A]">
                  {(data.customer_type?.returning_orders || 0).toLocaleString("es-ES")}
                </div>
                <p className="text-[11.5px] text-[#888888] mt-1.5">
                  {data.customer_type?.returning_orders_pct || 0}% del total validado
                </p>
                <div className="mt-3 bg-[#F0F0ED] h-1">
                  <div
                    className="bg-[#1A7A3C] h-1 transition-all"
                    style={{ width: `${data.customer_type?.returning_orders_pct || 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Profitability Block — Real Store Data (ledger format) */}
            {data.profitability_real && data.profitability_real.store_revenue > 0 && (
              <div
                className="border border-[#E8E8E5] rounded-lg bg-white overflow-hidden"
                style={{ borderLeft: "4px solid #1A7A3C" }}
                data-testid="profitability-block"
              >
                <div className="flex items-center justify-between px-5 py-3 bg-[#F7F6F3] border-b border-[#E8E8E5]">
                  <span className="label-swiss">Rentabilidad Real (Tienda)</span>
                  <span className="text-[12.5px] font-semibold text-[#1A7A3C]">
                    Beneficio Neto · €{data.profitability_real.net_profit.toLocaleString("es-ES", { minimumFractionDigits: 0 })}
                    {" · "}
                    {data.profitability_real.net_margin.toFixed(1)}%
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5">
                  {[
                    { label: "Ingresos", value: `€${data.profitability_real.store_revenue.toLocaleString("es-ES")}`, sub: `${data.profitability_real.conversions} pedidos` },
                    { label: "Coste Productos", value: `−€${data.profitability_real.product_cost.toLocaleString("es-ES")}` },
                    { label: "Gasto Ads", value: `−€${data.profitability_real.ad_spend.toLocaleString("es-ES")}` },
                    { label: "Margen Producto", value: `${data.profitability_real.product_margin.toFixed(1)}%`, color: "#1A7A3C" },
                    { label: "% Gasto/Ingresos", value: `${data.profitability_real.spend_percentage.toFixed(1)}%`, sub: data.profitability_real.spend_percentage < 30 ? "Excelente" : "Revisar" },
                  ].map((c, i) => (
                    <div key={c.label} className={`px-5 py-4 border-b md:border-b-0 ${i < 4 ? "md:border-r border-[#E8E8E5]" : ""}`}>
                      <div className="label-swiss mb-1.5">{c.label}</div>
                      <div className="font-mono text-[18px] font-semibold" style={{ color: c.color || "#0A0A0A" }}>
                        {c.value}
                      </div>
                      {c.sub && <div className="text-[11px] text-[#888888] mt-1">{c.sub}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Financial Status Banner — shown when fixed costs are configured */}
            {data.operating && data.operating.total_fixed_costs > 0 && (
              <FinancialStatusBanner operating={data.operating} />
            )}

            {/* Shipping Profitability */}
            {data.shipping && (data.shipping.revenue > 0 || data.shipping.cost > 0) && (
              <div className="border border-[#E8E8E5] rounded-lg bg-white overflow-hidden" data-testid="shipping-block">
                <div className="flex items-center justify-between px-5 py-3 bg-[#F7F6F3] border-b border-[#E8E8E5]">
                  <div className="flex items-center gap-3">
                    <Truck className="w-3.5 h-3.5 text-[#0A0A0A]" />
                    <div>
                      <span className="label-swiss">Rentabilidad de Envío</span>
                      <div className="text-[11.5px] text-[#888888] mt-0.5">
                        Ingreso cobrado al cliente vs. coste pagado al carrier
                      </div>
                    </div>
                  </div>
                  {data.shipping.missing_count > 0 && (
                    <span
                      className="flex items-center gap-1.5 text-[#C8192C] text-[11px] font-semibold"
                      data-testid="shipping-missing-warning"
                      title={`Meses sin coste configurado: ${data.shipping.missing_months.join(", ")}.`}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      Coste de envío ausente ({data.shipping.missing_count}{" "}
                      {data.shipping.missing_count === 1 ? "mes" : "meses"})
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4">
                  {[
                    { label: "Ingreso Envío", value: `€${data.shipping.revenue.toLocaleString("es-ES", { minimumFractionDigits: 2 })}`, sub: "cobrado al cliente", testId: "shipping-revenue" },
                    {
                      label: "Coste Envío",
                      value: data.shipping.cost > 0 ? `€${data.shipping.cost.toLocaleString("es-ES", { minimumFractionDigits: 2 })}` : "—",
                      sub: "pagado al carrier",
                      testId: "shipping-cost",
                    },
                    {
                      label: "Margen Envío",
                      value: data.shipping.cost > 0 ? `€${data.shipping.margin.toLocaleString("es-ES", { minimumFractionDigits: 2 })}` : "—",
                      sub: "revenue − coste",
                      color: data.shipping.cost > 0 ? (data.shipping.margin >= 0 ? "#1A7A3C" : "#C8192C") : "#888888",
                      testId: "shipping-margin",
                    },
                    {
                      label: "% sobre Ingresos",
                      value: data.shipping.cost > 0 ? `${data.shipping.margin_pct.toFixed(1)}%` : "—",
                      sub: "margen / ingresos totales",
                      color: data.shipping.cost > 0 ? (data.shipping.margin_pct >= 0 ? "#0A0A0A" : "#C8192C") : "#888888",
                      testId: "shipping-margin-pct",
                    },
                  ].map((c, i) => (
                    <div
                      key={c.label}
                      className={`px-5 py-4 border-b md:border-b-0 ${i < 3 ? "md:border-r border-[#E8E8E5]" : ""}`}
                    >
                      <div className="label-swiss mb-1.5">{c.label}</div>
                      <div className="font-mono text-[18px] font-semibold" style={{ color: c.color || "#0A0A0A" }} data-testid={c.testId}>
                        {c.value}
                      </div>
                      <div className="text-[11px] text-[#888888] mt-1">{c.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Returns & Cancellations — flat rows */}
            {data.returns && (data.returns.cancellations.amount > 0 || data.returns.refunds.amount > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className="border border-[#E8E8E5] rounded-lg bg-white p-5"
                  style={{ borderLeft: "4px solid #C8192C" }}
                  data-testid="cancellations-card"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <span className="label-swiss">Cancelaciones</span>
                      <div className="font-display font-black text-[24px] leading-none tracking-[-0.04em] mt-2 text-[#0A0A0A] font-mono">
                        €{data.returns.cancellations.amount.toLocaleString("es-ES", { minimumFractionDigits: 2 })}
                      </div>
                      <div className="text-[11.5px] text-[#888888] mt-1.5">
                        {data.returns.cancellations.count} pedidos
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[20px] font-bold text-[#C8192C]">
                        {data.returns.cancellations.pct.toFixed(1)}%
                      </div>
                      <div className="text-[11px] text-[#888888]">del bruto</div>
                    </div>
                  </div>
                </div>

                <div
                  className="border border-[#E8E8E5] rounded-lg bg-white p-5"
                  style={{ borderLeft: "4px solid #B45309" }}
                  data-testid="refunds-card"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <span className="label-swiss">Devoluciones</span>
                      <div className="font-display font-black text-[24px] leading-none tracking-[-0.04em] mt-2 text-[#0A0A0A] font-mono">
                        €{data.returns.refunds.amount.toLocaleString("es-ES", { minimumFractionDigits: 2 })}
                      </div>
                      <div className="text-[11.5px] text-[#888888] mt-1.5">
                        {data.returns.refunds.count} pedidos
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[20px] font-bold text-[#B45309]">
                        {data.returns.refunds.pct.toFixed(1)}%
                      </div>
                      <div className="text-[11px] text-[#888888]">del bruto</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top & Bottom Products Block */}
            <TopBottomProductsBlock
              topProducts={topBottomData?.top_products}
              bottomProducts={topBottomData?.bottom_products}
              loading={topBottomLoading}
            />

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Revenue & Spend Trend */}
              <div className="border border-[#E8E8E5] rounded-lg bg-white overflow-hidden">
                <div className="px-5 py-3 border-b border-[#E8E8E5]">
                  <span className="label-swiss">Tendencia</span>
                  <h3 className="text-[13px] font-semibold text-[#0A0A0A] mt-0.5">Ingresos vs Gasto</h3>
                </div>
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={data.daily_trend}>
                      <CartesianGrid strokeDasharray="2 4" stroke="#F0F0ED" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888888" }} stroke="#E8E8E5" />
                      <YAxis tick={{ fontSize: 10, fill: "#888888" }} stroke="#E8E8E5" />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="revenue" name="Ingresos" stroke="#C8192C" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="spend" name="Gasto" stroke="#0A0A0A" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Conversions by Day */}
              <div className="border border-[#E8E8E5] rounded-lg bg-white overflow-hidden">
                <div className="px-5 py-3 border-b border-[#E8E8E5]">
                  <span className="label-swiss">Volumen</span>
                  <h3 className="text-[13px] font-semibold text-[#0A0A0A] mt-0.5">Conversiones Diarias</h3>
                </div>
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={data.daily_trend}>
                      <CartesianGrid strokeDasharray="2 4" stroke="#F0F0ED" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888888" }} stroke="#E8E8E5" />
                      <YAxis tick={{ fontSize: 10, fill: "#888888" }} stroke="#E8E8E5" />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="conversions" name="Conversiones" fill="#0A0A0A" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Platforms — single editorial table */}
            <PlatformsTable
              platforms={data.platforms}
              onRowClick={(platform) => navigate(`/platform/${platform}`)}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-[#888888] mb-4 text-[13px]">
              No hay datos disponibles para el período seleccionado
            </p>
            <button
              onClick={() => { fetchAnalytics(true); fetchTopBottomProducts(true); }}
              className="px-4 py-2 bg-[#C8192C] hover:bg-[#A0141F] text-white text-[12.5px] font-medium rounded transition-colors"
            >
              Reintentar
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
